// frontend/src/components/staff/ScheduleShiftBlock.js
//
// Workforce — SCHED-SLICE1-E individual shift block on the calendar grid,
// with SCHED-OVERLAP-COLUMNS parallel-column + stacked-row layout support.
//
// Visual specs:
//   - docs/design/workforce-scheduling-slice-1-design.md §3.2 + §3.3 +
//     visual DoD §18.1
//   - docs/design/SCHED-CANCELLED-VISUAL-ADMIN-design.md §2 (cancelled state)
//   - docs/design/SCHED-OVERLAP-COLUMNS-design.md §2-§7 (layout + hover)
//
// Renders an absolutely-positioned overlay on a day-column with three
// visual states:
//   - Draft     = dashed (role color) border + 60% saturation + DRAFT pill
//   - Published = solid (role color) border + 100% saturation (no pill)
//   - Cancelled = 1.5px dashed --text-secondary border + 25% saturation
//                 fill + strike-through text + bottom-right CANCELLED pill.
//
// SCHED-OVERLAP-COLUMNS layout props (new):
//   - layout.mode = "single" | "column" | "stack-row"
//   - layout.columnIndex / layout.groupSize — column position within an
//     overlap group (column mode only)
//   - layout.dayCellWidthPx — measured day-cell width in px; consumer
//     uses this to derive per-column pixel width and text-mode (Standard
//     / Compact / Minimal) without forcing the parent to recompute
//     once per render
//   - layout.stackRowIndex / layout.stackRowCount — row position within
//     a stacked-row fallback (stack-row mode only)
//
// Cancelled shifts:
//   - Are NOT draggable (mousedown bypasses the parent's drag pickup;
//     cursor stays `pointer` because the block is still clickable for
//     forensic detail).
//   - Open the read-only Cancelled-detail modal on click, NOT EditShiftModal.
//     The parent (ScheduleBuilder) is responsible for routing the click
//     based on shift.status. ScheduleShiftBlock just emits onClick(shift).
//
// Pointer interactions delegated to the parent grid (mouse-down on this
// block fires `onPointerDown` callback with shift + e.clientY which the
// grid uses to start a drag). Click without drag opens the appropriate
// modal.
//
// Keyboard accessibility (visual spec §17): block is `tabIndex={0}` and
// Enter opens the modal. Drag-and-drop keyboard alternative is owned by
// the parent grid (`Space` + arrow keys).

import React from "react";
import {
  buildShiftAriaLabel,
  formatShiftTimeRange,
  formatShiftTimeRangeForMode,
  formatStaffNameMinimal,
  getRoleColor,
  resolveStaffDisplayName,
  formatRoleLabel,
  computeOverlapColumnWidth,
  selectTextMode,
  OVERLAP_LAYOUT_THRESHOLDS,
} from "./scheduleBuilderUtils";
import "./ScheduleShiftBlock.css";

// Trim a cancellation reason to a hover-tooltip-friendly length. Visual
// spec §2.2 specifies "first 80 chars of cancelReason".
const TOOLTIP_REASON_LIMIT = 80;
const truncateReasonForTooltip = (reason) => {
  if (typeof reason !== "string") return "";
  const trimmed = reason.trim();
  if (trimmed.length === 0) return "";
  if (trimmed.length <= TOOLTIP_REASON_LIMIT) return trimmed;
  return `${trimmed.slice(0, TOOLTIP_REASON_LIMIT)}...`;
};

// Format the cancelledAt timestamp for the hover tooltip. Visual spec §2.2:
// "Cancelled [Apr 21]. Reason: [first 80 chars]..." — short month + day.
const formatCancelledAtShort = (cancelledAt) => {
  if (!cancelledAt) return "";
  try {
    const d = new Date(cancelledAt);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch (_e) {
    return "";
  }
};

// Stack-row layout block height per visual spec §5.4 — 24px fixed row,
// not proportional to shift duration.
const STACK_ROW_HEIGHT_PX = 24;
// Stack-row stride: row + a 4px row gap so neighboring rows don't visually
// merge when multiple shifts land near the same time anchor. The visual
// spec implies a small inter-row separation; we use the standard 4px grid
// step (slice-1 spacing.xs).
const STACK_ROW_STRIDE_PX = 28;

const ScheduleShiftBlock = ({
  shift,
  staffById,
  geometry,
  onClick,
  onPointerDown,
  isDragging,
  isConflictHighlighted,
  layout,
}) => {
  // Hooks must run unconditionally on every render — the early-return
  // for missing shift / geometry happens AFTER the hook call below.
  // (react-hooks/rules-of-hooks).
  const [isHoverExpanded, setIsHoverExpanded] = React.useState(false);

  if (!shift || !geometry) return null;

  const profile = staffById && staffById.get ? staffById.get(String(shift.staffProfileId)) : null;
  const staffName = resolveStaffDisplayName(profile);
  const role = formatRoleLabel(shift.roleCode);
  const color = getRoleColor(shift.roleCode);
  const isDraft = shift.status === "draft";
  const isCancelled = shift.status === "cancelled";

  const resolvedLayout = layout || { mode: "single" };
  const layoutMode = resolvedLayout.mode || "single";
  const groupSize = Number(resolvedLayout.groupSize) || 1;
  const columnIndex = Number(resolvedLayout.columnIndex) || 0;
  const dayCellWidthPx = Number(resolvedLayout.dayCellWidthPx) || 0;

  // Per-column width + text mode derivation. For single + stack-row we
  // treat the column width as the full 90% inner band so text-mode lands
  // on Standard at typical widths.
  let columnWidthPx = 0;
  if (layoutMode === "column" && groupSize >= 2 && dayCellWidthPx > 0) {
    columnWidthPx = computeOverlapColumnWidth(dayCellWidthPx, groupSize);
  } else if (dayCellWidthPx > 0) {
    columnWidthPx = dayCellWidthPx * OVERLAP_LAYOUT_THRESHOLDS.DAY_COLUMN_INNER_PCT;
  }
  const textMode = layoutMode === "column" && groupSize >= 2 ? selectTextMode(columnWidthPx) : "standard";

  // Hover-expand state (spec §6) — only relevant for column mode at N>=2.
  // Stack-row + single render at full width already; hover does nothing
  // structural beyond the existing box-shadow lift.
  const supportsHoverExpand = layoutMode === "column" && groupSize >= 2;

  const handleEnterExpand = () => {
    if (supportsHoverExpand) setIsHoverExpanded(true);
  };
  const handleLeaveExpand = () => {
    if (supportsHoverExpand) setIsHoverExpanded(false);
  };

  // ARIA label — extend for overlap-group context (spec §14.1).
  let ariaLabel = buildShiftAriaLabel(shift, staffById);
  if (layoutMode === "column" && groupSize >= 2) {
    ariaLabel = `${ariaLabel}. Column ${columnIndex + 1} of ${groupSize} overlapping shifts.`;
  } else if (layoutMode === "stack-row" && Number(resolvedLayout.stackRowCount) >= 2) {
    const rowIdx = Number(resolvedLayout.stackRowIndex) || 0;
    const rowCount = Number(resolvedLayout.stackRowCount);
    ariaLabel = `${ariaLabel}. Row ${rowIdx + 1} of ${rowCount} stacked overlapping shifts.`;
  }

  // Cancel-state tooltip text — visual spec §2.2 + Scenario 8/9.
  // Defensive on a missing/empty reason (Scenario 9): omit the
  // "Reason: ..." suffix and fall through to just "Cancelled [date]".
  let tooltipTitle;
  if (isCancelled) {
    const cancelledOn = formatCancelledAtShort(shift.cancelledAt);
    const truncatedReason = truncateReasonForTooltip(shift.cancelReason);
    if (cancelledOn && truncatedReason) {
      tooltipTitle = `Cancelled ${cancelledOn}. Reason: ${truncatedReason}`;
    } else if (truncatedReason) {
      tooltipTitle = `Cancelled. Reason: ${truncatedReason}`;
    } else if (cancelledOn) {
      tooltipTitle = `Cancelled ${cancelledOn} — no reason recorded`;
    } else {
      tooltipTitle = "Cancelled — no reason recorded";
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (typeof onClick === "function") onClick(shift);
    }
  };

  const handleMouseDown = (e) => {
    // Cancelled shifts are not draggable — bypass the drag pickup.
    if (isCancelled) return;
    if (typeof onPointerDown === "function") {
      onPointerDown({ shift, event: e });
    }
  };

  const handleClick = (_e) => {
    // Only fire click if not part of a drag (parent tracks).
    if (isDragging) return;
    if (typeof onClick === "function") onClick(shift);
  };

  // -----------------------------------------------------------------
  // Geometry — three modes:
  //
  //   single      -> existing slice-1 absolute-positioned overlay,
  //                  5% left gutter + 90% width.
  //   column      -> percentage left + width derived from columnIndex
  //                  + groupSize. Hover expands to the inner band
  //                  (90% of day cell) with z-index = 10 (spec §6.1).
  //   stack-row   -> 24px-tall horizontal row at the shift's startTime
  //                  vertical anchor, 5% / 90% horizontal band, with a
  //                  small 28px stride between rows in the same group.
  // -----------------------------------------------------------------

  const innerPct = OVERLAP_LAYOUT_THRESHOLDS.DAY_COLUMN_INNER_PCT * 100; // 90
  const leftGutterPct = OVERLAP_LAYOUT_THRESHOLDS.DAY_COLUMN_LEFT_GUTTER_PCT * 100; // 5
  const gapPx = OVERLAP_LAYOUT_THRESHOLDS.INTER_COLUMN_GAP_PX;

  let positioning;
  if (layoutMode === "stack-row") {
    const rowIdx = Number(resolvedLayout.stackRowIndex) || 0;
    positioning = {
      top: `${Math.max(0, geometry.topPx + rowIdx * STACK_ROW_STRIDE_PX)}px`,
      height: `${STACK_ROW_HEIGHT_PX}px`,
      left: `${leftGutterPct}%`,
      width: `${innerPct}%`,
    };
  } else if (layoutMode === "column" && groupSize >= 2) {
    // Compute via calc() so the column math is independent of the
    // day cell's measured pixel width at render time (CSS handles the
    // composition; the helper above only drives text-mode + bail).
    // columnWidth = (W*innerPct% - (N-1)*GAP) / N
    // columnLeft  = leftGutterPct% + i * (columnWidth + GAP)
    // Encode columnWidth as a CSS calc() referencing 100% of the cell:
    //     calc((100% * innerPct/100 - (N-1)*GAP) / N)
    const widthCalc = `calc((100% * ${innerPct / 100} - ${(groupSize - 1) * gapPx}px) / ${groupSize})`;
    const leftCalc = `calc(${leftGutterPct}% + ${columnIndex} * ((100% * ${innerPct / 100} - ${(groupSize - 1) * gapPx}px) / ${groupSize} + ${gapPx}px))`;
    if (isHoverExpanded) {
      positioning = {
        top: `${geometry.topPx}px`,
        height: `${geometry.heightPx}px`,
        // Hover expand to 90% inner band (spec §6.2).
        left: `${leftGutterPct}%`,
        width: `${innerPct}%`,
        zIndex: 10,
      };
    } else {
      positioning = {
        top: `${geometry.topPx}px`,
        height: `${geometry.heightPx}px`,
        left: leftCalc,
        width: widthCalc,
      };
    }
  } else {
    // single / fallback
    positioning = {
      top: `${geometry.topPx}px`,
      height: `${geometry.heightPx}px`,
      left: `${leftGutterPct}%`,
      width: `${innerPct}%`,
    };
  }

  // Inline style:
  //   - Cancelled border color is the neutral --text-secondary (#6B6B6B)
  //     so it cannot be confused with a draft (role-color dashed) block.
  //   - Cancelled fill keeps the role color; the visible 25%-saturation
  //     desaturation is applied via the `--cancelled` CSS rule's
  //     `filter: saturate(0.25)` so it composites with the block bg.
  //   - Draft keeps the existing low-alpha overlay + dashed role-color border.
  const style = {
    ...positioning,
    background: isDraft ? `${color.bg}99` : color.bg,
    color: color.text,
    borderColor: isCancelled ? "#6B6B6B" : color.bg,
    borderStyle: isDraft || isCancelled ? "dashed" : "solid",
    borderWidth: isCancelled ? "1.5px" : "2px",
  };

  // CANCELLED pill visibility per spec §4.3 — hide when columnWidth < 60px.
  const hideCancelledPill =
    isCancelled &&
    layoutMode === "column" &&
    groupSize >= 2 &&
    !isHoverExpanded &&
    columnWidthPx > 0 &&
    columnWidthPx < OVERLAP_LAYOUT_THRESHOLDS.COMPACT_MIN_COLUMN_PX;

  const className = [
    "schedule-shift-block",
    isDraft ? "schedule-shift-block--draft" : null,
    isCancelled ? "schedule-shift-block--cancelled" : null,
    !isDraft && !isCancelled ? "schedule-shift-block--published" : null,
    isDragging ? "schedule-shift-block--dragging" : null,
    isConflictHighlighted ? "schedule-shift-block--conflict-flash" : null,
    layoutMode === "column" && groupSize >= 2 ? "schedule-shift-block--in-column" : null,
    layoutMode === "stack-row" ? "schedule-shift-block--stack-row" : null,
    isHoverExpanded ? "schedule-shift-block--hover-expanded" : null,
    layoutMode === "column" && groupSize >= 2
      ? `schedule-shift-block--text-${textMode}`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  // Choose the time-label string per text mode. Stack-row + single use
  // the standard MST form; column mode follows the legibility ladder.
  const showStandardSuffix = layoutMode !== "column" || groupSize < 2 || isHoverExpanded;
  const timeLabel = showStandardSuffix
    ? formatShiftTimeRange(shift)
    : formatShiftTimeRangeForMode(shift, textMode);

  // Staff label compaction in minimal mode (spec §5.1 + §5.2).
  const staffLabel =
    layoutMode === "column" && groupSize >= 2 && textMode === "minimal" && !isHoverExpanded
      ? formatStaffNameMinimal(staffName)
      : staffName;

  // Role visibility — hidden in compact + minimal column modes (spec §5.1).
  const hideRole =
    layoutMode === "column" &&
    groupSize >= 2 &&
    !isHoverExpanded &&
    (textMode === "compact" || textMode === "minimal");

  // Stack-row layout collapses time + staff + role onto a single line
  // (spec §5.4) — visually compact but uses the standard MST time form.
  const isStackRowSingleLine = layoutMode === "stack-row";

  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleEnterExpand}
      onMouseLeave={handleLeaveExpand}
      onFocus={handleEnterExpand}
      onBlur={handleLeaveExpand}
      aria-label={ariaLabel}
      title={tooltipTitle}
      data-testid={`schedule-shift-block-${shift._id || "new"}`}
      data-shift-id={shift._id || ""}
      data-shift-status={shift.status || ""}
      data-layout-mode={layoutMode}
      data-column-index={layoutMode === "column" ? String(columnIndex) : undefined}
      data-group-size={layoutMode === "column" ? String(groupSize) : undefined}
      data-text-mode={layoutMode === "column" && groupSize >= 2 ? textMode : undefined}
    >
      {isStackRowSingleLine ? (
        <span className="schedule-shift-block__stack-row-line">
          <span className="schedule-shift-block__time">{timeLabel}</span>
          <span className="schedule-shift-block__staff">{staffName}</span>
          <span className="schedule-shift-block__role">{role}</span>
        </span>
      ) : (
        <>
          <span className="schedule-shift-block__time">{timeLabel}</span>
          <span className="schedule-shift-block__staff">
            {staffLabel}
            {!hideRole && <span className="schedule-shift-block__role">{role}</span>}
          </span>
        </>
      )}
      {isDraft && (
        <span
          className="schedule-shift-block__draft-pill"
          aria-hidden="true"
          data-testid="schedule-shift-block-draft-pill"
        >
          DRAFT
        </span>
      )}
      {isCancelled && !hideCancelledPill && (
        <span
          className="schedule-shift-block__cancelled-pill"
          aria-hidden="true"
          data-testid="schedule-shift-block-cancelled-pill"
        >
          CANCELLED
        </span>
      )}
    </button>
  );
};

export default ScheduleShiftBlock;
